import PolicyLayout, { PolicyCallout, PolicyList, PolicySection } from "@/pages/policies/components/PolicyLayout";

export default function CancellationPage() {
  return (
    <PolicyLayout
      eyebrow="Policies"
      title="Cancellation Policy"
      updated="1 October 2026"
      intro="Plans change — and that is fine. This policy explains exactly when a rider gets a full refund, when a partial refund applies, and what it costs a host to cancel a confirmed trip. All refunds are returned to the original M-Pesa number used at checkout."
    >
      <PolicySection id="free-window" title="1. Free cancellation for riders">
        <p>
          How much notice you give before the trip start time decides your refund. Bookings made well ahead enjoy the
          most generous window.
        </p>
        <PolicyList
          items={[
            "Booked 24 hours or more before the start: cancel up to 24 hours before the start for a full refund.",
            "Booked within 24 hours of the start: cancel within 1 hour of booking for a full refund.",
            "Non-refundable trips: you still get a 24-hour grace period right after booking to cancel for a full refund.",
          ]}
        />
      </PolicySection>

      <PolicySection id="partial" title="2. Partial refunds & no-shows">
        <p>
          If you cancel after the free window, or you do not turn up, you receive a partial refund so the host is
          compensated for the days they held the car for you.
        </p>
        <PolicyList
          items={[
            "For trips longer than two days, the refund is the trip price and fees minus the average cost of one day.",
            "For shorter trips, the first day plus the service fee is non-refundable.",
            "If you never collect the car (a no-show), the same partial-refund rules apply.",
          ]}
        />
      </PolicySection>

      <PolicySection id="safety" title="3. Safety & cleanliness exceptions">
        <p>
          If the car is materially not as described, unsafe to drive, or significantly unclean at pickup, tell us before
          you drive away. In these cases we will help you into another car or issue a full refund.
        </p>
        <PolicyCallout icon="ri-shield-check-line">
          Report problems at the point of handover using the booking hub.&nbsp;Do not drive a car you believe is
          unsafe — that can affect your protection.
        </PolicyCallout>
      </PolicySection>

      <PolicySection id="early-return" title="4. Returning early">
        <p>
          If you bring the car back before the agreed end date, no refund or credit is issued for the unused days unless
          you requested and received approval to shorten the trip through Gari beforehand.
        </p>
      </PolicySection>

      <PolicySection id="host-cancel" title="5. When a host cancels">
        <p>
          A host cancelling a confirmed trip wrecks the rider&apos;s plans, so we apply fixed penalties that rise the
          closer the cancellation is to the start time.
        </p>
        <PolicyList
          items={[
            "Cancelling more than 24 hours before the start: a fixed host cancellation fee applies.",
            "Cancelling less than 24 hours before the start: a larger host cancellation fee applies.",
            "Not turning up within 30 minutes of the start time: the highest host cancellation fee applies and the rider is refunded in full.",
            "A notice is posted on the vehicle listing, and repeated cancellations can remove a host from the marketplace.",
          ]}
        />
        <p>
          Whenever a host cancels, the rider always receives a full refund of everything paid, including the service
          fee.
        </p>
      </PolicySection>

      <PolicySection id="gari-cancel" title="6. When Gari cancels">
        <p>
          We may occasionally need to cancel a booking ourselves — for example if we find a vehicle or account issue, or
          a safety or fraud concern. In that case the rider is refunded in full and we help them find an alternative car
          where possible.
        </p>
      </PolicySection>

      <PolicySection id="refund-timing" title="7. How and when refunds are paid">
        <PolicyList
          items={[
            "Refunds are sent back to the original M-Pesa number used at checkout.",
            "Approved refunds are usually processed within 3 to 5 business days.",
            "You will receive an in-app confirmation once a refund has been initiated.",
            "A service fee already earned on a completed or partially used trip is non-refundable.",
          ]}
        />
      </PolicySection>
    </PolicyLayout>
  );
}